import { Dispatch, SetStateAction, useCallback } from 'react';

import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { ObjectForSelection } from 'types/interfaces/ObjectForSelection';

import { GeographicalZoneService } from 'components/services/GeographicalZoneService';

import {
  DetailedInformationFormType
} from '../../constants/schemas';

type UseGeographicalZonesCardHandlersParams = {
  getValues: UseFormGetValues<DetailedInformationFormType>;
  setValue: UseFormSetValue<DetailedInformationFormType>;
  geographicalZones: ObjectForSelection[];
  setGeographicalZonesOptions: Dispatch<
    SetStateAction<ObjectForSelection[]>
  >;
  setLocalDirty: (dirty: boolean) => void;
};

export function useGeographicalZonesCardHandlers(
  params: UseGeographicalZonesCardHandlersParams
) {
  const {
    getValues,
    setValue,
    geographicalZones,
    setGeographicalZonesOptions,
    setLocalDirty
  } = params;

  const refreshGeographicalZonesOptions = useCallback(async () => {
    const geoResp =
      await GeographicalZoneService.getGeographicalZonesForSelection();
    const options = geoResp.data.isSuccess ? geoResp.data.value ?? [] : [];

    setGeographicalZonesOptions(options);

    const currentZones = (getValues('geographicalZones') ??
      []) as ObjectForSelection[];
    if (!currentZones.length) return;

    const optionsById = new Map(
      options.map((option) => [option.id?.toString(), option.label] as const)
    );

    const updatedZones = currentZones.map((zone) => {
      const nextLabel = optionsById.get(zone.id?.toString());
      return nextLabel ? { ...zone, label: nextLabel } : zone;
    });

    const hasChanges = updatedZones.some(
      (zone, index) => zone.label !== currentZones[index]?.label
    );

    if (hasChanges) {
      setValue('geographicalZones', updatedZones, {
        shouldDirty: false,
        shouldValidate: false
      });
    }
  }, [getValues, setGeographicalZonesOptions, setValue]);

  const handleGeographicalZonesChange = useCallback(
    (
      selection: ObjectForSelection | ObjectForSelection[] | null
    ) => {
      const next = Array.isArray(selection)
        ? selection
        : selection
          ? [selection]
          : [];
      setLocalDirty(true);
      setValue('geographicalZones', next, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setLocalDirty, setValue]
  );

  const handleDeleteGeographicalZoneTag = useCallback(
    (zoneId: string | number) => {
      setLocalDirty(true);
      const next = (geographicalZones ?? []).filter(
        (z) => z.id?.toString() !== zoneId.toString()
      );
      setValue('geographicalZones', next, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [geographicalZones, setLocalDirty, setValue]
  );

  return {
    refreshGeographicalZonesOptions,
    handleGeographicalZonesChange,
    handleDeleteGeographicalZoneTag
  };
}

